import { API_ENDPOINTS } from '../config/api';
import type {
  Course,
  AuthResponse,
  Testimonial,
  Module,
  SubModule,
  ModuleCourse,
  CourseProgress,
  CourseContent,
  SubModuleProgress,
  QuizQuestion,
  ProgressiveQuizQuestion,
  QuizSubmission,
  QuizSubmissionResult,
  QuizResult,
  QuizStatistics,
  UserProfile,
  ModuleProgress,
  CourseTitles,
  UserProgress,
  AdminUser,
  AdminQuizQuestion,
  AdminCourseComments,
  QuizStats,
  SubscriptionRequest, StripeCheckoutSession, UpgradePreview, AdminQuestionModule
} from '../types';

const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Une erreur est survenue');
  }
  return response.json();
};

const defaultFetchOptions: RequestInit = {
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json'
  }
};

export const api = {
  // Authentication
  authenticateWithGoogle: async (code: string): Promise<AuthResponse> => {
    try {
      const response = await fetch(API_ENDPOINTS.GOOGLE_CALLBACK, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Origin': window.location.origin
        }
      });

      if (!response.ok) {
        // Mock error response
        await new Promise(resolve => setTimeout(resolve, 500));
        throw new Error('Authentication failed');
      }

      // Mock successful response
      await new Promise(resolve => setTimeout(resolve, 1000));
      return {
        access_token: 'mock_jwt_token',
        token_type: 'bearer',
        user: {
          id: '1',
          email: 'john.doe@example.com',
          name: 'John Doe',
          picture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=800'
        }
      };
    } catch (error) {
      console.error('Google authentication failed:', error);
      throw error;
    }
  },

  // Modules
  getModules: async (): Promise<Module[]> => {
    const response = await fetch(API_ENDPOINTS.GET_MODULES, defaultFetchOptions);
    const modules = await handleResponse(response);
    return modules.sort((a, b) => a.id.localeCompare(b.id));
  },

  getSubModules: async (moduleId: string): Promise<SubModule[]> => {
    const response = await fetch(API_ENDPOINTS.GET_SUB_MODULES(moduleId), defaultFetchOptions);
    const subModules = await handleResponse(response);
    return subModules.sort((a, b) => a.id.localeCompare(b.id));
  },

  getModuleCourses: async (subModuleId: string): Promise<ModuleCourse[]> => {
    const response = await fetch(API_ENDPOINTS.GET_MODULE_COURSES(subModuleId), defaultFetchOptions);
    const courses = await handleResponse(response);
    return courses.sort((a, b) => a.id.localeCompare(b.id));
  },

  getSubModuleProgress: async (subModuleId: string): Promise<number> => {
    const response = await fetch(API_ENDPOINTS.GET_SUB_MODULE_PROGRESS(subModuleId), defaultFetchOptions);
    return handleResponse(response);
  },

  getModuleProgress: async (moduleId: string): Promise<SubModuleProgress[]> => {
    const response = await fetch(API_ENDPOINTS.GET_MODULE_PROGRESS(moduleId), defaultFetchOptions);
    const data = await handleResponse(response);
    return data.modules_progress || [];
  },

  // Courses
  getFeaturedCourses: async (): Promise<Course[]> => {
    const response = await fetch(API_ENDPOINTS.GET_FEATURED_COURSES, defaultFetchOptions);
    return handleResponse(response);
  },

  getCourseContent: async (courseId: string): Promise<CourseContent> => {
    const response = await fetch(API_ENDPOINTS.GET_COURSE_CONTENT(courseId), defaultFetchOptions);
    return handleResponse(response);
  },

  getCourseProgress: async (courseId: string): Promise<CourseProgress> => {
    const response = await fetch(API_ENDPOINTS.GET_COURSE_PROGRESS(courseId), defaultFetchOptions);
    return handleResponse(response);
  },

  updateCourseProgress: async (courseId: string, completed: boolean): Promise<CourseProgress> => {
    const progress = completed ? 100 : 0;
    const response = await fetch(`${API_ENDPOINTS.UPDATE_COURSE_PROGRESS(courseId)}?progress=${progress}`, {
      ...defaultFetchOptions,
      method: 'POST'
    });
    return handleResponse(response);
  },

  // Quiz
  getQuizQuestions: async (moduleIds: string[]): Promise<QuizQuestion[]> => {
    const response = await fetch(API_ENDPOINTS.GET_QUIZ_QUESTIONS, {
      ...defaultFetchOptions,
      method: 'POST',
      body: JSON.stringify({ moduleIds })
    });
    return handleResponse(response);
  },

  getProgressiveQuizQuestions: async (): Promise<ProgressiveQuizQuestion[]> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/questions/progressive`, defaultFetchOptions);
    return handleResponse(response);
  },

  getFreeQuizQuestions: async (difficulty: 'easy' | 'medium' | 'difficult' | 'mix', modules: string[]): Promise<ProgressiveQuizQuestion[]> => {
    const params = new URLSearchParams();
    params.append('difficulty', difficulty);
    modules.forEach(module => params.append('modules', module));

    const response = await fetch(`${import.meta.env.VITE_API_URL}/questions/free?${params.toString()}`, defaultFetchOptions);
    return handleResponse(response);
  },

  submitQuizResult: async (submission: QuizSubmission): Promise<QuizSubmissionResult> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/qcm/submit`, {
      ...defaultFetchOptions,
      method: 'POST',
      body: JSON.stringify(submission)
    });
    return handleResponse(response);
  },

  saveQuizResult: async (result: QuizResult): Promise<void> => {
    const response = await fetch(API_ENDPOINTS.SAVE_QUIZ_RESULT, {
      ...defaultFetchOptions,
      method: 'POST',
      body: JSON.stringify(result)
    });
    return handleResponse(response);
  },

  getQuizStatistics: async (): Promise<QuizStatistics[]> => {
    const response = await fetch(API_ENDPOINTS.GET_QUIZ_STATISTICS, defaultFetchOptions);
    return handleResponse(response);
  },

  getQuizStats: async (): Promise<QuizStats> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/qcm/stats`, defaultFetchOptions);
    return handleResponse(response);
  },

  canTakeQuiz: async (): Promise<{ can_take: boolean; reason?: string }> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/qcm/can-take`, defaultFetchOptions);
    return handleResponse(response);
  },

  getFreeQuizCount: async (): Promise<number> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/qcm/free-count`, defaultFetchOptions);
    const data = await handleResponse(response);
    return data.remaining;
  },

  // User
  getUserProfile: async (): Promise<UserProfile> => {
    const response = await fetch(API_ENDPOINTS.GET_USER_PROFILE, defaultFetchOptions);
    const profile = await handleResponse(response);
    console.log('User profile from API:', profile);
    return profile;
  },

  getUserProgress: async (): Promise<{ experience: number; level: string; modules_progress: ModuleProgress[] }> => {
    const response = await fetch(API_ENDPOINTS.GET_USER_PROGRESS, defaultFetchOptions);
    const data = await handleResponse(response);
    return data;
  },

  checkFirstConnection: async (): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.CHECK_FIRST_CONNECTION, defaultFetchOptions);
    return handleResponse(response);
  },

  setUserObjective: async (objective: string): Promise<void> => {
    const response = await fetch(API_ENDPOINTS.SET_USER_OBJECTIVE(objective), {
      ...defaultFetchOptions,
      method: 'POST'
    });
    return handleResponse(response);
  },

  deleteAccount: async (): Promise<void> => {
    const response = await fetch(API_ENDPOINTS.DELETE_ACCOUNT, {
      ...defaultFetchOptions,
      method: 'DELETE'
    });
    return handleResponse(response);
  },

  previewUpgrade: async (plan: string): Promise<UpgradePreview> => {
    const response = await fetch(API_ENDPOINTS.PREVIEW_UPGRADE, {
      ...defaultFetchOptions,
      method: 'POST',
      body: JSON.stringify({ new_plan: plan })
    });
    return handleResponse(response);
  },

  cancelSubscription: async (): Promise<void> => {
    const response = await fetch(API_ENDPOINTS.CANCEL_SUBSCRIPTION, {
      ...defaultFetchOptions,
      method: 'POST'
    });
    return handleResponse(response);
  },

  checkInvoiceStatus: async (): Promise<{ paid: boolean }> => {
    const response = await fetch(API_ENDPOINTS.CHECK_INVOICE_STATUS, defaultFetchOptions);
    return handleResponse(response);
  },

  // Testimonials
  getTestimonials: async (): Promise<Testimonial[]> => {
    const response = await fetch(API_ENDPOINTS.GET_TESTIMONIALS, defaultFetchOptions);
    return handleResponse(response);
  },

  // Subscriptions
  createSubscription: async (data: SubscriptionRequest): Promise<StripeCheckoutSession> => {
    const response = await fetch(API_ENDPOINTS.CREATE_SUBSCRIPTION, {
      ...defaultFetchOptions,
      method: 'POST',
      body: JSON.stringify({
        plan: data.plan,
        success_url: data.successUrl,
        cancel_url: data.cancelUrl
      })
    });
    return handleResponse(response);
  },

  getUserStats: async (userId: string): Promise<QuizStats> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/${userId}/stats`, defaultFetchOptions);
    return handleResponse(response);
  },

  // Comments
  getCourseComments: async (courseId: string): Promise<Comment[]> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/courses/${courseId}/comments`, defaultFetchOptions);
    return handleResponse(response);
  },

  addCourseComment: async (courseId: string, content: string): Promise<Comment> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/courses/${courseId}/comments`, {
      ...defaultFetchOptions,
      method: 'POST',
      body: JSON.stringify({ content })
    });
    return handleResponse(response);
  },

  // Admin endpoints
  getAllComments: async (): Promise<AdminCourseComments[]> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/courses/admin_comments`, defaultFetchOptions);
    return handleResponse(response);
  },

  updateCourseContent: async (courseId: string, data: { content?: string; video_url?: string }): Promise<void> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/courses/${courseId}/content`, {
      ...defaultFetchOptions,
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getAllQuizQuestions: async (): Promise<AdminQuestionModule[]> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/questions`, defaultFetchOptions);
    return handleResponse(response);
  },

  updateQuizQuestion: async (questionId: string, data: Partial<AdminQuizQuestion>): Promise<void> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/questions/${questionId}`, {
      ...defaultFetchOptions,
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  deleteQuizQuestion: async (questionId: string): Promise<void> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/questions/${questionId}`, {
      ...defaultFetchOptions,
      method: 'DELETE'
    });
    return handleResponse(response);
  },

  createQuizQuestion: async (data: Omit<AdminQuizQuestion, 'id'>): Promise<AdminQuizQuestion> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/questions/`, {
      ...defaultFetchOptions,
      method: 'POST',
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getAllUsers: async (search?: string): Promise<AdminUser[]> => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/users${params}`, defaultFetchOptions);
    return handleResponse(response);
  },

  getUserAdminProgress: async (userId: string): Promise<UserProgress> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/${userId}/progress`, defaultFetchOptions);
    return handleResponse(response);
  },

  getCourseTitles: async (): Promise<CourseTitles> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/courses/titles`, defaultFetchOptions);
    return handleResponse(response);
  },

  markCommentAsSeen: async (commentId: string): Promise<void> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/courses/comments/${commentId}/mark-seen`, {
      ...defaultFetchOptions,
      method: 'POST'
    });
    return handleResponse(response);
  }
};